import { getDebugFlag } from '@src/utility/debug';
import { LogManager } from '@src/utility/Logger';
import { getPatch } from 'fast-array-diff';

import { ClipTranscriptSegment } from '../models/ClipTranscriptSegment';
import { VideoTranscriptElement } from '../models/elements/VideoTranscriptElement';
import { TranscriptTrack } from '../models/TranscriptTrack';
import { type TranscriptTextToken, type TranscriptToken } from '../types';
import { IdGenerator, IdGeneratorEntityType } from './IdGenerator';

import type { TranscriptElementLegacy } from '@src/types.ts/transcript';
import type { Patch } from 'fast-array-diff';

const logger = LogManager.getLogger('editor:transcriptValidations');

export const DEFAULT_MINIMUM_TOKEN_DURATION = 0.1;
export const NON_REMOVED_TEXT_TOKENS_TOLERANCE = 5; // number of non-removed text tokens that can differ before we consider it misaligned

export class TranscriptValidationError extends Error {
  constructor(
    message: string,
    public info: string,
  ) {
    super(message);
    this.name = 'TranscriptValidationError';
  }
}

export const fixZeroDurationTokens = (
  tokens: TranscriptElementLegacy[],
  minDuration = DEFAULT_MINIMUM_TOKEN_DURATION,
) => {
  let indexesAdjusted = [];

  const result = tokens
    .map(token => ({ ...token }))
    .filter(
      token =>
        token.type === 'punct' ||
        (token.type === 'text' && token.ts != null && token.end_ts != null),
    );

  const getDuration = (token: TranscriptElementLegacy): number | null => {
    if (token.type !== 'text' || token.ts == null || token.end_ts == null) {
      return null;
    }
    return token.end_ts - token.ts;
  };

  let breakLoop = false;
  const firstTextStart = result.find(
    (token): token is TranscriptTextToken => token.type === 'text',
  )?.ts;
  const lastTextEnd = result
    .filter((token): token is TranscriptTextToken => token.type === 'text')
    .at(-1)?.end_ts;

  if (firstTextStart == null || lastTextEnd == null) {
    console.debug('fixZeroDurationTokens: no text timestamps found');
  }

  // Step A: Process all tokens except the last one
  for (let i = 0; i < result.length - 1; i++) {
    const token = result[i];
    const duration = getDuration(token);
    if (
      token.type === 'text' &&
      duration !== null &&
      duration < minDuration - 1e-4 &&
      token.ts !== null
    ) {
      indexesAdjusted.push(i);
      token.end_ts = token.ts + minDuration;

      // Check for overlap with next text token and adjust if needed
      for (let j = i + 1; j < result.length; j++) {
        const nextToken = result[j];
        if (
          nextToken.type === 'text' &&
          nextToken.ts !== null &&
          token.end_ts !== null
        ) {
          if (nextToken.ts < token.end_ts) {
            nextToken.ts = token.end_ts;
          }
          break;
        }
      }
    }
  }

  if (indexesAdjusted.length > 0) {
    console.debug(
      `fixZeroDurationTokens: forward path - increased duration for ${indexesAdjusted.length} tokens`,
    );
  }

  // Step B: Process the last token

  let indexesAdjustedReverse = [];

  if (result.length > 0) {
    for (let i = result.length - 1; i > 0; i--) {
      if (breakLoop) break;
      const token = result[i];
      const duration = getDuration(token);
      if (
        token.type === 'text' &&
        duration !== null &&
        duration < minDuration - 1e-4 &&
        token.end_ts !== null &&
        firstTextStart !== null
      ) {
        indexesAdjustedReverse.push(i);
        token.ts = Math.max(token.end_ts - minDuration, firstTextStart ?? 0);

        // Check for overlap with prev text token and adjust if needed
        for (let j = i - 1; j >= 0; j--) {
          const prevToken = result[j];
          if (
            prevToken.type === 'text' &&
            prevToken.end_ts !== null &&
            token.ts !== null
          ) {
            if (prevToken.end_ts > token.ts) {
              prevToken.end_ts = token.ts;
            } else {
              breakLoop = true;
              break;
            }
          }
        }
      } else {
        break;
      }
    }
  }

  if (indexesAdjustedReverse.length > 0) {
    console.debug(
      `fixZeroDurationTokens: backward path - increased duration for ${indexesAdjustedReverse.length} tokens`,
    );
  }

  // Final validation and warning for tokens that still have duration < minDuration
  for (let i = 0; i < result.length; i++) {
    const token = result[i];
    const duration = getDuration(token);
    if (
      token.type === 'text' &&
      duration !== null &&
      duration < minDuration - 1e-4
    ) {
      console.debug(
        `fixZeroDurationTokens: cannot increase duration for token index ${i}`,
        token,
      );
    }
  }

  return result;
};

/**
 * Ensure no two elements carry the same `id` (PLAT-10876).
 *
 * Element ids are the stable join key the backend translation writes into each
 * phrase's `originalElementIds`. That join assumes the phrase list *partitions*
 * the source elements, so a transcript where two elements share an id fails the
 * backend's pre-persist guard and the entire translation is rejected —
 * permanently, for every retry, until the transcript is repaired.
 *
 * Ids are minted by the hex `TranscriptToken` generator, whose free-id pool is
 * only as complete as the collection it was last seeded from. Correct seeding
 * (see `TranscriptTrack.fromVideoTrackAndTranscript`) is the prevention; this
 * is the write-side backstop for transcripts a mis-seeded generator already
 * corrupted. The first element to claim an id keeps it — a later claimant gets
 * a freshly minted one. Any consumer resolving that id was already resolving an
 * ambiguous reference, so re-stamping the later one strictly improves matters.
 *
 * Returns the input array unchanged when there is nothing to fix.
 */
export const fixDuplicateElementIds = <T extends TranscriptElementLegacy>(
  elements: readonly T[],
): readonly T[] => {
  const claimedIds = new Set<string>();
  const duplicateIndexes: number[] = [];
  elements.forEach((element, index) => {
    if (!element.id) return;
    if (claimedIds.has(element.id)) duplicateIndexes.push(index);
    else claimedIds.add(element.id);
  });
  if (duplicateIndexes.length === 0) return elements;

  const generator = IdGenerator.get(
    IdGeneratorEntityType.TranscriptToken,
    'hex',
  );
  generator.setupFromCollection(elements);

  const result = [...elements];
  for (const index of duplicateIndexes) {
    // The generator is seeded from this very list, but it is a session-wide
    // singleton — re-roll rather than trust it to never repeat.
    let freshId = generator.getNextIdHex();
    while (claimedIds.has(freshId)) freshId = generator.getNextIdHex();
    claimedIds.add(freshId);
    result[index] = { ...result[index], id: freshId };
  }

  logger.warn('fixDuplicateElementIds: reassigned duplicate element ids', {
    duplicateCount: duplicateIndexes.length,
    elementCount: elements.length,
  });
  return result;
};

/*
- Parse existing transcription
- Fix zero duration tokens
- Validate parsed, detect mismatch with input
- Reconstruct mismatched parts from original
- Handle non-linear order of clips

*/

/*
-> Tr0 (original transcript) -> P'1: Fix zero durations -> P'2: Split multi-word tokens -> Tr2


-> T0 (current transcript) -> P1: assign media_ts to removed words
-> T1 -> P2: Fix zero durations
-> T2 -> P3: Reconstruct removed timeline clips -> P4: construct segments
-> CS4 -> P5: Get Segments constructed transcript
-> T5 -> P6: Compare with T0 for missing, mismatched words

-> CS6 mismatching segments -> P7: Reconstruct mismatched segments from original (Tr2)
-> CS7 -> T7 -> P8: Split multi-word tokens
--------------------------------------

P1: Asign media_ts to removed words
Old transcripts don't have valid timestamps assigned for removed words, but media_ts can be looked up from original transcript by initial_index

P2: Fix zero durations
Extend token duration to minimal (MIN_DURATION per word in token, i.e. for multi-word tokens):
- Split into lists by state
- For each token in each list:
  - if zero duration
    - if 1st in the list: extend to right
    - if last in the list: extend to left
    - if not 1st or last: extend to right, if not possible - extend to left

P3: Reconstruct removed timeline clips
Linear order:
 - for each gap in media between video elements add removed clip

Non-linear (Build minimal representation?):
 - find longest ascending sequence => build removed segments between them, rest of the elements - insertions

P4: Build Transcript Track

P6: Compare with T0 for missing, mismatched words
 - Longest Common Subsequence? = diff-tool

 - split T0 into lists by state (like in P2)
 - find common subsequences between segments and lists
 - find indexes that do not appear in a segment or a list if common subsequence is not a full list/segment

P7: Reconstruct segments from original
 - find common subsequences between CS4 and CS7 segments:
 - take segments from CS7 as primary, validating list
 - compare one by one - those not having common subsequence

*/

export const recreateTrackFromOriginalTranscript = (
  videoTrack: TranscriptTrack,
  originalTranscript: readonly TranscriptElementLegacy[],
) => {
  const recreatedVideoTranscriptElements = videoTrack.elements.map(el => {
    const clipTranscriptSegment = ClipTranscriptSegment.createFromElements(
      originalTranscript,
      el.getMediaStartTime(),
      el.getMediaEndTime(),
      el.getMediaStartTime(),
      el.getMediaEndTime(),
    );
    return new VideoTranscriptElement({
      ...el.getState(),
      clipTranscriptSegment: clipTranscriptSegment,
    });
  });

  const nonRemovedRecreatedTranscriptTokens =
    recreatedVideoTranscriptElements.flatMap(el =>
      el.getActiveTranscriptElements(),
    );

  // will add removed clips segments
  return TranscriptTrack.fromVideoTrackAndTranscript(
    videoTrack,
    nonRemovedRecreatedTranscriptTokens,
    originalTranscript,
    true,
  );
};

export const verifyTranscriptConsistencyQuick = (
  inputTranscript: readonly TranscriptElementLegacy[],
  originalTranscript: readonly TranscriptElementLegacy[],
  loadedTrack: TranscriptTrack,
) => {
  try {
    const fullLoadedTranscript = loadedTrack.transcriptSegmentElements.flatMap(
      segment => segment.getActiveTranscriptElements(),
    );

    // Only compare loaded vs input because input can naturally differ from original becasuse it is edited (e.g. has words replaced)

    const nonRemovedTextTokensToOriginalDiffCount = 0;

    /* if patchLoadedToOriginal have little to none changes to apply we can be sure that loaded
    transcript is mostly-to-fully aligned with video (such if the transcript would be re-created)
    */

    const nonRemovedTextTokensDiffCount = compareTranscriptsLengths(
      [...inputTranscript],
      fullLoadedTranscript,
    );

    /* if patchInputToCurrent have little to none changes to apply we can beleive that loading
    transcript into the new transcript system works correctly and transcript is compatible with
    both legacy and new transcript systems.
    If loaded transcript diverges from input it can
    indicate that the input transcript has problems and loading it into the new transcript
    system will change output transcript (not safe for migration, needs review)
    */

    let resultStatus: 'ok' | 'warning' | 'error' = 'ok';
    let resultInfo = '';

    if (nonRemovedTextTokensDiffCount > 0) {
      resultStatus = 'warning';
      resultInfo += `Input vs Loaded: ${nonRemovedTextTokensDiffCount} non-removed text tokens differ. \n`;
    }
    if (nonRemovedTextTokensToOriginalDiffCount > 0) {
      resultStatus = 'warning';
      resultInfo += `Loaded vs Recreated: ${nonRemovedTextTokensToOriginalDiffCount} non-removed text tokens differ. \n`;
    }
    if (nonRemovedTextTokensDiffCount > NON_REMOVED_TEXT_TOKENS_TOLERANCE) {
      resultStatus = 'error';
      resultInfo += `Input vs Loaded: ${nonRemovedTextTokensDiffCount} non-removed text tokens differ exceeds tolerance (${NON_REMOVED_TEXT_TOKENS_TOLERANCE}). \n`;
    }
    if (
      nonRemovedTextTokensToOriginalDiffCount >
      NON_REMOVED_TEXT_TOKENS_TOLERANCE
    ) {
      resultStatus = 'error';
      resultInfo += `Loaded vs Recreated: ${nonRemovedTextTokensToOriginalDiffCount} non-removed text tokens differ exceeds tolerance (${NON_REMOVED_TEXT_TOKENS_TOLERANCE}). \n`;
    }

    return {
      status: resultStatus,
      info: resultInfo,
    };
  } catch (error) {
    console.error(
      'verifyTranscriptConsistency: Error creating transcript track from original, skipping:',
      error,
    );
    return {
      status: 'error',
      info: 'Error creating transcript track from original',
    };
  }
};

export const verifyTranscriptConsistency = (
  inputTranscript: readonly TranscriptElementLegacy[],
  originalTranscript: readonly TranscriptElementLegacy[],
  loadedTrack: TranscriptTrack,
) => {
  try {
    const fullLoadedTranscript = loadedTrack.transcriptSegmentElements.flatMap(
      segment => segment.getActiveTranscriptElements(),
    );

    const clonedTrack = loadedTrack.clone();
    const recreateTrackFromOriginal = recreateTrackFromOriginalTranscript(
      clonedTrack,
      originalTranscript,
    );

    const recreatedFromOriginalTranscript =
      recreateTrackFromOriginal.transcriptSegmentElements.flatMap(segment =>
        segment.getActiveTranscriptElements(),
      );

    const patchLoadedToOriginal = compareTranscripts(
      fullLoadedTranscript,
      recreatedFromOriginalTranscript,
    );

    /* if patchLoadedToOriginal have little to none changes to apply we can be sure that loaded
    transcript is mostly-to-fully aligned with video (such if the transcript would be re-created)
    */

    if (getDebugFlag('logTranscriptValidation')) {
      console.log(
        'verifyTranscriptConsistency: compare loaded to recreated transcript',
        patchLoadedToOriginal,
      );
      console.log('verifyTranscriptConsistency: full transcript comparison:');
      logPatch(patchLoadedToOriginal.patchFull, 'verifyTranscriptConsistency');

      console.log('verifyTranscriptConsistency: non-removed text comparison:');
      logPatch(
        patchLoadedToOriginal.patchNotRemoved,
        'verifyTranscriptConsistency',
      );
    }

    const patchInputToCurrent = compareTranscripts(
      [...inputTranscript],
      fullLoadedTranscript,
    );

    /* if patchInputToCurrent have little to none changes to apply we can beleive that loading
    transcript into the new transcript system works correctly and transcript is compatible with
    both legacy and new transcript systems.
    If loaded transcript diverges from input it can
    indicate that the input transcript has problems and loading it into the new transcript
    system will change output transcript (not safe for migration, needs review)
    */
    if (getDebugFlag('logTranscriptValidation')) {
      console.log(
        'verifyTranscriptConsistency: compare input to loaded transcript',
        patchInputToCurrent,
      );
      console.log(
        'verifyTranscriptConsistency: full transcript comparison with original:',
      );
      logPatch(patchInputToCurrent.patchFull, 'verifyTranscriptConsistency');

      console.log(
        'verifyTranscriptConsistency: non-removed text comparison with original:',
      );
      logPatch(
        patchInputToCurrent.patchNotRemoved,
        'verifyTranscriptConsistency',
      );
    }

    let resultStatus: 'ok' | 'warning' | 'error' = 'ok';
    let resultInfo = '';
    const nonRemovedTextTokensDiff = getTextElementsDiff(
      patchInputToCurrent.patchNotRemoved,
    );
    const nonRemovedTextTokensDiffToOriginal = getTextElementsDiff(
      patchLoadedToOriginal.patchNotRemoved,
    );

    if (nonRemovedTextTokensDiff.length > 0) {
      resultStatus = 'warning';
      resultInfo += `Input vs Loaded: ${nonRemovedTextTokensDiff.length} non-removed text tokens differ. \n`;
    }
    if (nonRemovedTextTokensDiffToOriginal.length > 0) {
      resultStatus = 'warning';
      resultInfo += `Loaded vs Recreated: ${nonRemovedTextTokensDiffToOriginal.length} non-removed text tokens differ. \n`;
    }
    if (nonRemovedTextTokensDiff.length > NON_REMOVED_TEXT_TOKENS_TOLERANCE) {
      resultStatus = 'error';
      resultInfo += `Input vs Loaded: ${nonRemovedTextTokensDiff.length} non-removed text tokens differ exceeds tolerance (${NON_REMOVED_TEXT_TOKENS_TOLERANCE}). \n`;
    }
    if (
      nonRemovedTextTokensDiffToOriginal.length >
      NON_REMOVED_TEXT_TOKENS_TOLERANCE
    ) {
      resultStatus = 'error';
      resultInfo += `Loaded vs Recreated: ${nonRemovedTextTokensDiffToOriginal.length} non-removed text tokens differ exceeds tolerance (${NON_REMOVED_TEXT_TOKENS_TOLERANCE}). \n`;
    }

    return {
      status: resultStatus,
      info: resultInfo,
    };
  } catch (error) {
    console.error(
      'verifyTranscriptConsistency: Error creating transcript track from original, skipping:',
      error,
    );
    return {
      status: 'error',
      info: 'Error creating transcript track from original',
    };
  }
};

const getTextElementsDiff = (
  patch: Patch<TranscriptToken | TranscriptElementLegacy>,
) => {
  return patch.flatMap(pathItem =>
    pathItem.items.filter(item => item.type === 'text'),
  );
};

export const logPatch = (
  patch: Patch<TranscriptToken | TranscriptElementLegacy>,
  label: string,
) => {
  const logDiff = (
    label: string,
    type: string,
    items: (TranscriptToken | TranscriptElementLegacy)[],
  ) => {
    const parts = [];
    const styles = [];

    const baseStyle = type === 'add' ? 'color: green;' : 'color: red;';

    for (const token of items) {
      if (token.state === 'removed') {
        // Add styled token
        parts.push('%c' + token.value + '%c');
        styles.push(baseStyle + ' text-decoration: line-through;');
        styles.push(baseStyle); // reset back to base style
      } else {
        // Normal token (no extra %c)
        parts.push(token.value);
      }
    }

    const prefix = type === 'add' ? '+ ' : '- ';
    console.log(`%c${prefix}${parts.join('')}`, baseStyle, ...styles);
  };

  // log added and removed in green and red taking only token value, if token state is removed show it with strikethrough
  patch.forEach(pathItem => {
    // patchItem has type (add, remove) and items (TranscriptToken). Token state can also be removed, same token state text should be concatenated in one string to show in logs
    logDiff(label, pathItem.type, pathItem.items);
  });
};

export const compareTranscripts = (
  transcript1: TranscriptElementLegacy[],
  transcript2: TranscriptElementLegacy[],
) => {
  const patchFull = getPatch(transcript1, transcript2, (t1, t2) => {
    const isRemoved = (t: TranscriptElementLegacy) =>
      t.state === 'removed' || t.state === 'cut';
    return (
      t1.initial_index === t2.initial_index && isRemoved(t1) === isRemoved(t2)
    );
  });

  const patchNotRemoved = getPatch(
    transcript1.filter(el => el.state !== 'removed' && el.state !== 'cut'),
    transcript2.filter(el => el.state !== 'removed' && el.state !== 'cut'),
    (t1, t2) => t1.initial_index === t2.initial_index,
  );

  return { patchFull, patchNotRemoved };
};

const compareTranscriptsLengths = (
  transcript1: Pick<TranscriptToken, 'state' | 'type' | 'value'>[],
  transcript2: Pick<TranscriptToken, 'state' | 'type' | 'value'>[],
) => {
  return Math.abs(
    getNumberOfWordsInTranscript(transcript1) -
      getNumberOfWordsInTranscript(transcript2),
  );
};

const getNumberOfWordsInTranscript = (
  transcript: Pick<TranscriptToken, 'state' | 'type' | 'value'>[],
) => {
  return transcript.reduce((count, token) => {
    if (
      token.state !== 'removed' &&
      token.state !== 'cut' &&
      token.type === 'text' &&
      token.value
    ) {
      // splitting by one ore more spaces
      return count + token.value.trim().split(/\s+/).length;
    }
    return count;
  }, 0);
};
